// Motor de Fases de Calidad — genérico por programa de cliente (Natura STD-06482 y otros futuros).
// Toda la config (metas, umbrales, reglas) vive en un registro de `programas_calidad_cliente`,
// nunca hardcodeada acá. Este módulo solo implementa el MECANISMO; los NÚMEROS los define cada
// programa. Ver NATURA_STD_06482_SEED más abajo para los valores reales de la norma (Figuras 4/5/6).

export const FASES = ["bronce", "plata", "oro"];
export const SERIES = ["A", "B"];
export const INDICADORES = ["ppm", "auditoriaTecnica", "cpk", "cumplimientoRNC", "reincidenciaRNC"];

// ─── SEED — Natura STD-06482 v6.0 ──────────────────────────────────────────
// Números reales de Figuras 4/5/6 de la norma (confirmados por Adur). Cada fase guarda dos
// valores por indicador donde la norma los da ("implementacion" / "monitoreo") — para la
// evaluación mensual (cerrarMes) usamos SIEMPRE "monitoreo" (el umbral de régimen permanente,
// más estricto), y guardamos "implementacion" solo como referencia editable — NO hay un estado
// automático de "período de implementación" que alterne entre los dos; es una simplificación
// deliberada para no inventar un mecanismo que la norma no describió con precisión. Revisar si
// hace falta modelar eso más adelante.
//
// ppm NO es un número fijo: es reducción % vs la Meta Anual de ppm de cada família (dato que
// Natura define bilateralmente, todavía no lo tenemos — familia.metaAnualPpm queda en null hasta
// que se cargue). El target real de cada mes se calcula como metaAnualPpm * (1 - ppmReduccionPct/100).
//
// Cpk: en Bronce no hay meta individual (solo el gate de portfolio ya existente, 30% líneas
// ≥1,33, para ascender a Plata). En Plata/Oro, la meta individual mensual es 1,33 — además,
// el ascenso Plata→Oro ahora exige TAMBIÉN 100% de líneas con Cpk≥1,33 (no solo "CEP en 100% de
// líneas" como se había armado antes) — ver evaluarAscenso.
//
// Se ignora la cláusula de "vidrios CP1" — no aplica (la pyme no provee envases de vidrio).
export const NATURA_STD_06482_SEED = {
  codigo: "STD-06482",
  cliente: "Natura",
  nombre: "Programa de Calidad Asegurada",
  version: "6.0",
  fases: {
    bronce: {
      metas: {
        cpk: null,
        cumplimientoRNC: { implementacion: 70, monitoreo: 80 },
        reincidenciaRNC: { implementacion: 8, monitoreo: 4 },
        ppmReduccionPct: 30,
      },
      umbralAuditoriaAscenso: 80,
      umbralAuditoriaMonitoreo: 85,
    },
    plata: {
      metas: {
        cpk: 1.33,
        cumplimientoRNC: { implementacion: 80, monitoreo: 95 },
        reincidenciaRNC: { implementacion: 4, monitoreo: 0 },
        ppmReduccionPct: 65,
      },
      umbralAuditoriaAscenso: 85,
      umbralAuditoriaMonitoreo: 95,
      umbralAuditoriaMantenimiento: 85,
    },
    oro: {
      metas: {
        cpk: 1.33,
        cumplimientoRNC: { implementacion: 95, monitoreo: 95 },
        reincidenciaRNC: { implementacion: 0, monitoreo: 0 },
        ppmReduccionPct: 65,
      },
      umbralAuditoriaMonitoreo: 95,
      umbralAuditoriaMantenimiento: 95,
    },
  },
  ascenso: {
    bronceAPlata: { pctLineasCpkMinimo: 30, cpkMinimo: 1.33, mesesConsecutivosDentroMeta: 3 },
    plataAOro: { pctLineasConCEP: 100, pctLineasCpkMinimo: 100, cpkMinimo: 1.33, mesesConsecutivosDentroMeta: 4 },
  },
  retroceso: {
    general: { mesesToleranciaFueraMeta: 2 },
    ppm: { mesesLimiteRevertir: 3, toleranciaOcurrenciasPor12Meses: 1 },
    auditoriaTecnica: { diasPlanAccion: 15, mesesLimiteRevertir: 3 },
    cpk: { mesesLimiteRevertir: 3 },
    alertaTransversal: { mesesSinRevertir: 3 },
  },
  serieB: { mesesVentanaGracia: 3 },
};

// ─── ESTADO INICIAL ─────────────────────────────────────────────────────────
// Serie A/B, ventana de gracia y su historial NO viven acá — son estado del PROGRAMA (proveedor-
// cliente completo), no de cada família. Ver estadoInicialSeriePrograma más abajo.
export function estadoInicialFamilia(idFamiliaLinea, idProgramaCliente) {
  return {
    idFamiliaLinea,
    idProgramaCliente,
    participaPrograma: true,
    faseActual: "bronce",
    fechaUltimoCambioFase: null,
    rachaDentroMetaGeneral: 0,
    alertaRiesgoBidding: { activa: false, indicadores: [] },
    indicadores: Object.fromEntries(INDICADORES.map(k => [k, estadoInicialIndicador()])),
    historialFases: [],
  };
}

// Estado de Serie A/B — uno solo por programa (programas_calidad_cliente.data.estadoSerie), no
// por família. `ultimoPeriodoDescontado` evita que la ventana de gracia se descuente más de una
// vez en el mismo período si varias familias cierran su mes ese mismo mes (ver
// descontarVentanaGraciaSiCorresponde).
export function estadoInicialSeriePrograma() {
  return {
    serie: "A",
    fechaUltimoCambioSerie: null,
    ventanaGraciaSerieB: { activa: false, mesesRestantes: null, motivo: null },
    ultimoPeriodoDescontado: null,
    historialSerie: [],
  };
}

function estadoInicialIndicador() {
  return { fueraMetaConsecutivos: 0, enPlanAccion: false, mesesEnPlanAccion: 0, ocurrencias12m: [], ultimoValor: null };
}

// ─── ¿EL VALOR DEL MES ESTÁ DENTRO DE META? ─────────────────────────────────
// meta === null significa "sin dato todavía" (ej. metaAnualPpm de la família no cargada) -> se
// considera dentro de meta, nunca penalizamos por un número que no tenemos.
export function dentroDeMeta(indicador, valor, meta) {
  if (meta === null || meta === undefined || valor === null || valor === undefined) return true;
  if (indicador === "ppm") return valor <= meta;
  if (indicador === "cpk") return valor >= meta;
  if (indicador === "cumplimientoRNC") return valor >= meta;
  if (indicador === "reincidenciaRNC") return valor <= meta;
  return true;
}

// metaAnualPpm: dato propio de cada família (Meta Anual de ppm pactada con Natura, aún no provista).
function metasDeFase(programa, fase, metaAnualPpm) {
  const base = programa.fases[fase]?.metas || {};
  const reduccionPct = base.ppmReduccionPct;
  const ppmMeta = (metaAnualPpm !== null && metaAnualPpm !== undefined && reduccionPct !== null && reduccionPct !== undefined)
    ? metaAnualPpm * (1 - reduccionPct / 100)
    : null;
  return {
    ppm: ppmMeta,
    cpk: base.cpk ?? null,
    cumplimientoRNC: base.cumplimientoRNC?.monitoreo ?? null,
    reincidenciaRNC: base.reincidenciaRNC?.monitoreo ?? null,
  };
}

// ─── EVALUAR UN INDICADOR PARA EL MES ───────────────────────────────────────
function evaluarIndicador(nombre, estadoPrevio, valorMes, dentro, periodo) {
  const est = { ...estadoPrevio, ultimoValor: valorMes };
  if (dentro) {
    est.fueraMetaConsecutivos = 0;
    est.enPlanAccion = false;
    est.mesesEnPlanAccion = 0;
    return est;
  }
  est.fueraMetaConsecutivos = estadoPrevio.fueraMetaConsecutivos + 1;
  est.enPlanAccion = true;
  est.mesesEnPlanAccion = est.fueraMetaConsecutivos;

  if (nombre === "ppm" && est.fueraMetaConsecutivos === 2) {
    est.ocurrencias12m = [...estadoPrevio.ocurrencias12m, { periodo }];
  }
  return est;
}

function ocurrenciasPpmEnVentana12m(ocurrencias12m, periodoActual) {
  const [anio, mes] = periodoActual.split("-").map(Number);
  const fechaActual = new Date(anio, mes - 1, 1);
  return ocurrencias12m.filter(o => {
    const [a, m] = o.periodo.split("-").map(Number);
    const meses = (fechaActual.getFullYear() - a) * 12 + (fechaActual.getMonth() - (m - 1));
    return meses >= 0 && meses < 12;
  });
}

// ─── ¿ESTE INDICADOR DISPARA UN RETROCESO ESTE MES? ─────────────────────────
// Devuelve null (no dispara) o { directoABronce: bool, motivo }.
// Decisión propia (no especificada 100%): si varios indicadores disparan retroceso el mismo mes,
// se aplica UNA sola bajada de fase (la peor: directo a Bronce le gana a "retrocede una fase").
function evaluarDisparoRetroceso(nombre, estadoIndicador, faseActual, programa, periodo) {
  const reglas = programa.retroceso;

  if (nombre === "ppm") {
    const ocurrenciasVigentes = ocurrenciasPpmEnVentana12m(estadoIndicador.ocurrencias12m, periodo);
    if (ocurrenciasVigentes.length >= 2) {
      return { directoABronce: false, motivo: "ppm: 2ª vez en 12 meses móviles que se alcanzan 2 meses fuera de meta — retroceso inmediato" };
    }
    if (estadoIndicador.fueraMetaConsecutivos >= reglas.ppm.mesesLimiteRevertir) {
      return { directoABronce: false, motivo: `ppm: ${estadoIndicador.fueraMetaConsecutivos} meses fuera de meta sin revertir al 3er mes` };
    }
    return null;
  }

  if (nombre === "auditoriaTecnica") {
    // Bronce/Plata evaluando ascenso: no migran, pero tampoco retroceden por este indicador.
    if (faseActual === "bronce") return null;
    if (estadoIndicador.fueraMetaConsecutivos >= reglas.auditoriaTecnica.mesesLimiteRevertir) {
      const valorMuyBajo = estadoIndicador.ultimoValor !== null && estadoIndicador.ultimoValor < programa.fases.bronce.umbralAuditoriaAscenso;
      return { directoABronce: valorMuyBajo, motivo: `auditoría técnica: mínimo de mantenimiento no recuperado en ${estadoIndicador.fueraMetaConsecutivos} meses` };
    }
    return null;
  }

  if (nombre === "cpk") {
    // Cpk se evalúa por evento de auditoría anual (causas especiales), no mes a mes por sí solo;
    // el contador igual se lleva mensualmente una vez que el evento anual lo marca "fuera de meta".
    if (estadoIndicador.fueraMetaConsecutivos >= reglas.cpk.mesesLimiteRevertir) {
      return { directoABronce: false, motivo: `Cpk: causa especial sin revertir en ${estadoIndicador.fueraMetaConsecutivos} meses desde la auditoría anual` };
    }
    return null;
  }

  // Regla general (cumplimientoRNC, reincidenciaRNC, o cualquier indicador sin regla propia)
  if (estadoIndicador.fueraMetaConsecutivos >= reglas.general.mesesToleranciaFueraMeta) {
    return { directoABronce: false, motivo: `${nombre}: fuera de meta ${estadoIndicador.fueraMetaConsecutivos} meses (plan de acción sin resultado)` };
  }
  return null;
}

function faseAnterior(fase) {
  const i = FASES.indexOf(fase);
  return i > 0 ? FASES[i - 1] : "bronce";
}

// ─── ASCENSO ─────────────────────────────────────────────────────────────────
// portfolioStats: { pctLineasCpkMinimo(cpkMin), pctLineasConCEP } — estadísticas del conjunto de
// familias activas (participaPrograma=true) bajo el MISMO programa de cliente. Decisión propia:
// interpreté "≥30% líneas/SKU con Cpk≥1,33" y "CEP en 100% de líneas" como gates de portfolio
// (todo el programa), no del SKU individual evaluado — revisar si Natura lo mide distinto.
// Plata→Oro exige AMBAS señales que se dieron por separado en la conversación (CEP implementado
// Y Cpk≥1,33), no una u otra — decisión propia para no descartar ninguna de las dos instrucciones.
function evaluarAscenso(estado, programa, portfolioStats) {
  if (estado.faseActual === "bronce") {
    const r = programa.ascenso.bronceAPlata;
    if (estado.rachaDentroMetaGeneral >= r.mesesConsecutivosDentroMeta &&
        portfolioStats.pctLineasCpkMinimo(r.cpkMinimo) >= r.pctLineasCpkMinimo) {
      return { nuevaFase: "plata", motivo: `Ascenso Bronce→Plata: ${estado.rachaDentroMetaGeneral} meses dentro de meta + ${r.pctLineasCpkMinimo}%+ líneas con Cpk≥${r.cpkMinimo}` };
    }
  }
  if (estado.faseActual === "plata") {
    const r = programa.ascenso.plataAOro;
    if (estado.rachaDentroMetaGeneral >= r.mesesConsecutivosDentroMeta &&
        portfolioStats.pctLineasConCEP() >= r.pctLineasConCEP &&
        portfolioStats.pctLineasCpkMinimo(r.cpkMinimo) >= r.pctLineasCpkMinimo) {
      return { nuevaFase: "oro", motivo: `Ascenso Plata→Oro: ${estado.rachaDentroMetaGeneral} meses dentro de meta + CEP en ${r.pctLineasConCEP}% de líneas + Cpk≥${r.cpkMinimo} en ${r.pctLineasCpkMinimo}% de líneas` };
    }
  }
  return null;
}

// ─── CIERRE DE MES ───────────────────────────────────────────────────────────
// Solo maneja fase (Bronce/Plata/Oro) — la Serie A/B se maneja aparte a nivel de programa, ver
// descontarVentanaGraciaSiCorresponde, que hay que llamar una vez por período (no una vez por
// família) desde el caller.
// estado: fila de fase_familia_linea.data (la previa) de la família que se está cerrando
// valoresMes: { ppm, auditoriaTecnica, cpk, cumplimientoRNC, reincidenciaRNC }
// programa: registro de programas_calidad_cliente.data
// portfolioStats: ver evaluarAscenso
// periodo: "YYYY-MM"
// metaAnualPpm: dato propio de la família (puede ser null si Natura todavía no lo pasó)
export function cerrarMes({ estado, valoresMes, programa, portfolioStats, periodo, metaAnualPpm }) {
  if (!estado.participaPrograma) {
    return { estado: { ...estado, faseActual: "bronce" }, eventos: ["participaPrograma=false: motor no corre, fase fija en Bronce (evaluación por historial individual de SKU)"] };
  }

  const metas = metasDeFase(programa, estado.faseActual, metaAnualPpm);
  const eventos = [];
  const nuevosIndicadores = {};
  let todosDentroDeMeta = true;
  let disparoRetroceso = null; // { directoABronce, motivo } — el peor de los disparados, para decidir la fase
  const motivosDisparados = []; // TODOS los motivos que dispararon retroceso este mes, aunque se aplique una sola bajada
  const indicadoresFueraSinRevertir3m = [];

  for (const nombre of INDICADORES) {
    const valor = valoresMes[nombre] ?? null;
    const dentro = dentroDeMeta(nombre, valor, metas[nombre]);
    if (!dentro) todosDentroDeMeta = false;

    const estIndicador = evaluarIndicador(nombre, estado.indicadores[nombre], valor, dentro, periodo);
    nuevosIndicadores[nombre] = estIndicador;

    if (estIndicador.fueraMetaConsecutivos >= programa.retroceso.alertaTransversal.mesesSinRevertir) {
      indicadoresFueraSinRevertir3m.push(nombre);
    }

    if (!dentro) {
      const disparo = evaluarDisparoRetroceso(nombre, estIndicador, estado.faseActual, programa, periodo);
      if (disparo) {
        motivosDisparados.push(disparo.motivo);
        if (!disparoRetroceso || (disparo.directoABronce && !disparoRetroceso.directoABronce)) {
          disparoRetroceso = disparo; // se queda con el peor caso si hay más de un disparo
        }
      }
    }
  }

  let faseActual = estado.faseActual;
  let fechaUltimoCambioFase = estado.fechaUltimoCambioFase;
  let historialFases = estado.historialFases;
  let rachaDentroMetaGeneral = todosDentroDeMeta ? estado.rachaDentroMetaGeneral + 1 : 0;

  if (disparoRetroceso) {
    const nuevaFase = disparoRetroceso.directoABronce ? "bronce" : faseAnterior(estado.faseActual);
    if (nuevaFase !== faseActual) {
      const motivoCompleto = motivosDisparados.join(" | ");
      faseActual = nuevaFase;
      fechaUltimoCambioFase = periodo;
      historialFases = [...historialFases, { fase: nuevaFase, fecha: periodo, motivo: motivoCompleto, tipo: "retroceso" }];
      rachaDentroMetaGeneral = 0;
      eventos.push(`Retroceso a ${nuevaFase}: ${motivoCompleto}`);
    }
  } else {
    const ascenso = evaluarAscenso({ ...estado, rachaDentroMetaGeneral }, programa, portfolioStats);
    if (ascenso) {
      faseActual = ascenso.nuevaFase;
      fechaUltimoCambioFase = periodo;
      historialFases = [...historialFases, { fase: ascenso.nuevaFase, fecha: periodo, motivo: ascenso.motivo, tipo: "ascenso" }];
      rachaDentroMetaGeneral = 0;
      eventos.push(ascenso.motivo);
    }
  }

  const alertaRiesgoBidding = {
    activa: indicadoresFueraSinRevertir3m.length > 0,
    indicadores: indicadoresFueraSinRevertir3m,
  };
  if (alertaRiesgoBidding.activa) {
    eventos.push(`Alerta de riesgo de pérdida de volumen bidding (informativa): ${indicadoresFueraSinRevertir3m.join(", ")}`);
  }

  const nuevoEstado = {
    ...estado,
    faseActual, fechaUltimoCambioFase, historialFases, rachaDentroMetaGeneral,
    alertaRiesgoBidding,
    indicadores: nuevosIndicadores,
  };

  return { estado: nuevoEstado, eventos };
}

// ─── REGLA 2 — DESVÍO CRÍTICO (instantánea, no cuenta meses) ────────────────
// Alcance: PROVEEDOR-CLIENTE completo — se aplica a TODAS las familias activas del mismo
// programa, no solo a la família donde se originó el desvío. `estadosFamilias` debe venir ya
// filtrado a las familias con participaPrograma=true del programa correspondiente; la família de
// origen queda registrada en cada historial para trazabilidad (idFamiliaLineaOrigen).
export function registrarDesvioCritico(estadosFamilias, motivo, periodo, idFamiliaLineaOrigen) {
  return estadosFamilias.map(estado => ({
    ...estado,
    faseActual: "bronce",
    fechaUltimoCambioFase: periodo,
    rachaDentroMetaGeneral: 0,
    historialFases: [...estado.historialFases, {
      fase: "bronce", fecha: periodo, tipo: "desvio_critico",
      motivo: `Desvío crítico (Segunda Regla, aplicado a todo el proveedor-cliente): ${motivo}`,
      idFamiliaLineaOrigen,
    }],
  }));
}

// ─── REGLA 3 — RESULTADO DE AUDITORÍA DE MONITOREO (Serie A/B) ─────────────
// Serie A/B es UN SOLO estado por programa (proveedor-cliente completo), no por família — opera
// sobre programa.estadoSerie directamente (no sobre un array de estados de família como antes).
// resultado: "satisfactorio" | "parcialmente_satisfactorio" | "no_satisfactorio"
export function registrarResultadoAuditoriaMonitoreo(estadoSerie, resultado, programa, periodo, idFamiliaLineaOrigen) {
  if (resultado === "no_satisfactorio") {
    if (estadoSerie.ventanaGraciaSerieB.activa) return estadoSerie; // ya estaba corriendo, no se reinicia
    return {
      ...estadoSerie,
      ventanaGraciaSerieB: { activa: true, mesesRestantes: programa.serieB.mesesVentanaGracia, motivo: `Auditoría de monitoreo no satisfactoria en família ${idFamiliaLineaOrigen} (${periodo})` },
    };
  }
  // satisfactorio o parcialmente_satisfactorio: cancela ventana de gracia y recupera Serie A
  return {
    ...estadoSerie,
    serie: "A",
    fechaUltimoCambioSerie: estadoSerie.serie === "B" ? periodo : estadoSerie.fechaUltimoCambioSerie,
    historialSerie: estadoSerie.serie === "B" ? [...estadoSerie.historialSerie, { serie: "A", fecha: periodo, motivo: `Auditoría de monitoreo ${resultado} (família origen ${idFamiliaLineaOrigen})` }] : estadoSerie.historialSerie,
    ventanaGraciaSerieB: { activa: false, mesesRestantes: null, motivo: null },
  };
}

// Descuenta un mes de la ventana de gracia Serie B — hay que llamarla UNA vez por período, sin
// importar cuántas familias del programa cierren su mes ese período (guardia:
// ultimoPeriodoDescontado). Si varias familias cierran el mismo período, la primera descuenta y
// las siguientes son no-op para ese período.
export function descontarVentanaGraciaSiCorresponde(estadoSerie, periodo) {
  if (!estadoSerie.ventanaGraciaSerieB.activa) return { estadoSerie, evento: null };
  if (estadoSerie.ultimoPeriodoDescontado === periodo) return { estadoSerie, evento: null };

  const mesesRestantes = estadoSerie.ventanaGraciaSerieB.mesesRestantes - 1;
  if (mesesRestantes <= 0) {
    return {
      estadoSerie: {
        ...estadoSerie,
        serie: "B",
        fechaUltimoCambioSerie: periodo,
        historialSerie: [...estadoSerie.historialSerie, { serie: "B", fecha: periodo, motivo: "Ventana de gracia de 3 meses agotada tras auditoría de monitoreo no satisfactoria" }],
        ventanaGraciaSerieB: { activa: false, mesesRestantes: null, motivo: null },
        ultimoPeriodoDescontado: periodo,
      },
      evento: "Cae a Serie B: ventana de gracia agotada",
    };
  }
  return {
    estadoSerie: { ...estadoSerie, ventanaGraciaSerieB: { ...estadoSerie.ventanaGraciaSerieB, mesesRestantes }, ultimoPeriodoDescontado: periodo },
    evento: null,
  };
}

// ─── LOCK DE CIERRE DE MES (control interno, no es de la norma) ─────────────
export function nuevaFilaIndicadores({ idFamiliaLinea, idProgramaCliente, periodo, valoresMes, usuario }) {
  return {
    id: Date.now(),
    idFamiliaLinea, idProgramaCliente, periodo,
    ...valoresMes,
    cerrado: true,
    fechaCierre: new Date().toISOString(),
    cerradoPor: usuario?.nombre || "Sistema",
    historialReapertura: [],
  };
}

export function reabrirMes(fila, motivo, usuario) {
  if (!motivo?.trim()) throw new Error("motivoReapertura es obligatorio para reabrir un mes cerrado");
  return {
    ...fila,
    cerrado: false,
    historialReapertura: [...(fila.historialReapertura || []), { motivo, usuario: usuario?.nombre || "Sistema", fecha: new Date().toISOString() }],
  };
}

// Reabrir y volver a cerrar NO recalcula la fase automáticamente (ver limitación documentada en
// el reporte de auditoría). `advertenciaFaseNoRecalculada:true` queda grabado en la fila para que
// la UI muestre un aviso visible y persistente, no solo un mensaje de una vez.
export function cerrarFilaReabierta(fila, usuario) {
  return { ...fila, cerrado: true, fechaCierre: new Date().toISOString(), cerradoPor: usuario?.nombre || "Sistema", advertenciaFaseNoRecalculada: true };
}

// ─── CHECKLIST DE AUDITORÍA TÉCNICA ─────────────────────────────────────────
// Los 6 apartados son los que pasó Adur. Los ÍTEMS de cada apartado (el contenido real del
// checklist de Natura, con su puntaje 10/5/0) están en anexos que todavía no tenemos — quedan
// vacíos a propósito, listos para cargar cuando lleguen. No se inventa contenido de auditoría.
export const NATURA_CHECKLIST_APARTADOS_SEED = [
  "Sistema de Gestión de Calidad", "Inventario", "Laboratorios", "Trazabilidad", "Mantenimiento", "Proceso",
].map(nombre => ({ nombre, items: [] }));

export const CATEGORIAS_MATERIAL = ["producto_terminado", "empaque", "materia_prima"];

export function calcularPctChecklist(respuestas) {
  if (!respuestas?.length) return null;
  const obtenido = respuestas.reduce((acc, r) => acc + (Number(r.puntaje) || 0), 0);
  const posible = respuestas.length * 10; // escala 10/5/0 -> máximo por ítem es 10
  return posible > 0 ? (obtenido / posible) * 100 : null;
}

// tipoAuditoria: "ascenso" | "monitoreo" | "mantenimiento"
export function umbralAprobacionChecklist(programa, fase, tipoAuditoria) {
  const f = programa.fases[fase];
  if (!f) return null;
  if (tipoAuditoria === "ascenso") return f.umbralAuditoriaAscenso ?? null;
  if (tipoAuditoria === "monitoreo") return f.umbralAuditoriaMonitoreo ?? null;
  return f.umbralAuditoriaMantenimiento ?? null;
}

export function evaluarResultadoChecklist(pct, umbral) {
  if (pct === null || umbral === null || umbral === undefined) return "sin_umbral_configurado";
  return pct >= umbral ? "aprobado" : "no_aprobado";
}

// ─── ÚLTIMO VALOR CONOCIDO DE UN INDICADOR ──────────────────────────────────
// Bug real que esto corrige: si el gate de portfolio (pctLineasCpkMinimo/pctLineasConCEP) solo
// mira la fila del período EXACTO que se está cerrando, el resultado depende de en qué orden se
// cierra cada família ese mes (la primera ve un portfolio casi vacío, la última lo ve completo).
// Usando el último valor conocido de cada família hasta el período inclusive, el cálculo es
// independiente del orden de cierre.
// indicadoresMensuales: array de filas indicadores_mensuales_familia.data (de cualquier família)
export function ultimoValorConocido(indicadoresMensuales, idFamiliaLinea, campo, periodoLimite) {
  const filas = indicadoresMensuales
    .filter(i => i.idFamiliaLinea === idFamiliaLinea && i.periodo <= periodoLimite && i[campo] !== null && i[campo] !== undefined)
    .sort((a, b) => b.periodo.localeCompare(a.periodo));
  return filas.length ? filas[0][campo] : null;
}

// ─── ESTADÍSTICAS DE PORTFOLIO (para los gates de ascenso) ──────────────────
// familiasEstado: array de filas fase_familia_linea.data del mismo programa, participaPrograma=true
// capabilidadPorFamilia: Map idFamiliaLinea -> [{cpk}] (hoy se completa a mano desde los
// indicadores mensuales cargados — Capabilidad.js todavía no vincula sus estudios a una família).
export function construirPortfolioStats(familiasEstado, capabilidadPorFamilia) {
  const activas = familiasEstado.filter(f => f.participaPrograma);
  return {
    pctLineasCpkMinimo(cpkMin) {
      if (activas.length === 0) return 0;
      const cumplen = activas.filter(f => {
        const estudios = capabilidadPorFamilia.get(f.idFamiliaLinea) || [];
        return estudios.some(e => e.cpk !== null && e.cpk >= cpkMin);
      });
      return (cumplen.length / activas.length) * 100;
    },
    pctLineasConCEP() {
      if (activas.length === 0) return 0;
      const conCEP = activas.filter(f => (capabilidadPorFamilia.get(f.idFamiliaLinea) || []).length > 0);
      return (conCEP.length / activas.length) * 100;
    },
  };
}
